import { Config } from '@/config';
import { Sentry } from '@/plugins/sentry';
import { ApiService } from '@/services/api.service';
import { StorageService, TOKEN_KEY } from '@/services/storage.service';
import { toPromise } from '@/util/promises';
import * as qs from 'query-string';

export const AuthService = {
  /**
   * Login the user and store the access token to StorageService.
   *
   * @returns access_token
   **/
  login(email, clearPassword, rememberMe) {
    const remember = qs.stringify({ rememberMe });

    return ApiService.post(`${Config.api.auth.url}auth?${remember}`, { email, clearPassword })
      .then((response) => response.data)
      .then(({ user, token }) => {
        StorageService.saveRememberMe(rememberMe);
        StorageService.saveToken(token);
        StorageService.saveCurrentUser(user);
        ApiService.setHeader();
        Sentry.setUser(user);

        return { user, token };
      });
  },

  validate() {
    return ApiService.get(`${Config.api.auth.url}auth/validate`).then((response) => {
      const user = response.data;

      StorageService.saveCurrentUser(user);
      Sentry.setUser(user);

      return user;
    });
  },

  checkTokenValidity(token) {
    const tokenUrl = qs.stringify({ token });
    const url = `${Config.api.auth.url}auth/recover-password/validate?${tokenUrl}`;
    const requestData = {
      method: 'get',
      url,
    };

    return ApiService.customRequest(requestData);
  },

  resetPassword(email) {
    const url = `${Config.api.auth.url}auth/recover-password`;
    const requestData = {
      method: 'post',
      url,
      data: {
        email,
      },
    };

    return ApiService.customRequest(requestData);
  },

  changePassword(password, token) {
    return ApiService.post(`${Config.api.auth.url}auth/change-password`, { token, password })
      .then((response) => response.data)
      .then((data) => {
        const { user, token } = data;
        if (user && token) {
          StorageService.saveToken(token);
          StorageService.saveCurrentUser(user);
          ApiService.setHeader();
          Sentry.setUser(user);
        }
        return data;
      });
  },

  /**
   * Logout the current user by removing the token from storage.
   *
   * Will also remove `Authorization Bearer <token>` header from future requests.
   **/
  logout() {
    return toPromise(() => {
      // Remove the token and remove Authorization header from Api Service as well
      StorageService.removeToken();
      StorageService.removeCurrentUser();
      StorageService.removeRememberMe();
      ApiService.removeHeader();
      Sentry.setUser(null);
    });
  },

  /**
   * Automatically refresh token at configured interval
   * Will store new token in localStorage and update headers for subsequent requests
   */
  refreshTokenRoutine() {
    AuthService.refreshToken().catch(() => {
      // swallow error
    });

    setInterval(() => {
      AuthService.refreshToken().catch(() => {
        // swallow error
      });
    }, Config.api.auth.refreshTokenInterval);
  },

  listenStorageChanges() {
    // Listen to storage changes from other tabs
    window.addEventListener('storage', function (event) {
      if (event.key === TOKEN_KEY) {
        if (event.newValue === null) {
          // Logout
          location.reload();
        } else if (event.oldValue === null) {
          // Login
          location.reload();
        }
      }
    });
  },

  async refreshToken() {
    if (StorageService.getToken()) {
      const rememberMe = qs.stringify({ rememberMe: StorageService.getRememberMe() || false });
      const requestData = {
        method: 'get',
        url: `${Config.api.auth.url}auth/refresh?${rememberMe}`,
      };

      const response = await ApiService.customRequest(requestData);

      StorageService.saveToken(response.data.token);
      ApiService.setHeader();
    }
  },
};
